import React from 'react'
import { Package, Plus } from 'lucide-react'
import { Button } from '../ui/Button'
import type { InventoryItem } from '../../types'
import type { ProductColor, ProductSize } from '../../utils/productGrouping'
import type { ShipmentMode } from './ui/mode-theme'
import { MODE_THEME } from './ui/mode-theme'
import { StockBadge } from './ui/stock-badge'

interface ProductVariantCardProps {
  item: InventoryItem
  colors: ProductColor[]
  sizes: ProductSize[]
  shouldShowPricing: boolean
  shipmentMode: ShipmentMode
  locationQty: number
  reservedQty: number
  availableQty: number
  onAdd: (item: InventoryItem) => void
}

export function ProductVariantCard({
  item,
  colors,
  sizes,
  shouldShowPricing,
  shipmentMode,
  locationQty,
  reservedQty,
  availableQty,
  onAdd
}: ProductVariantCardProps) {
  const hasReservations = reservedQty > 0
  const itemColor = colors.find(c => c.id === item.color_id)
  const itemSize = sizes.find(s => s.id === item.size_id)
  const modeTheme = MODE_THEME[shipmentMode]

  return (
    <div
      className={`group relative bg-white dark:bg-gray-800 border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 flex flex-col ${
        hasReservations
          ? 'border-amber-300 dark:border-amber-600'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      <div className="aspect-square bg-gray-100 dark:bg-gray-900 relative overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center text-gray-400 dark:text-gray-600">
              <Package className="h-12 w-12 mx-auto mb-2" />
              <span className="text-xs">No Image</span>
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
          {locationQty <= 0 ? (
            <StockBadge quantity={0} showLabel size="sm" />
          ) : (
            <StockBadge quantity={locationQty} size="sm" />
          )}
          {hasReservations && (
            <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/50 text-xs font-bold text-amber-700 dark:text-amber-300 rounded-full shadow-sm border border-amber-300 dark:border-amber-600">
              {reservedQty} reserved
            </span>
          )}
        </div>
      </div>

      <div className="p-3 flex flex-col flex-grow">
        <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-1 line-clamp-2 min-h-[2.5rem]">
          {item.name}
        </h3>

        <div className="space-y-1.5 mb-2">
          {itemColor && (
            <div className="flex items-center gap-1.5">
              <div
                className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600 shadow-sm flex-shrink-0"
                style={{ backgroundColor: itemColor.hex_code || '#ccc' }}
              />
              <span
                className="text-xs font-medium truncate"
                style={{ color: itemColor.hex_code || '#666' }}
              >
                {itemColor.name}
              </span>
            </div>
          )}

          {itemSize && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 rounded">
                {itemSize.name}
              </span>
            </div>
          )}

          {item.category && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {item.category}
            </p>
          )}

          {hasReservations && (
            <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
              {availableQty} available
            </p>
          )}
        </div>

        <div className="mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
          {shouldShowPricing && item.sell_price != null && item.sell_price > 0 && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-lg font-bold text-green-600 dark:text-green-400">
                {'\u20AC'}{item.sell_price.toFixed(2)}
              </span>
            </div>
          )}

          <Button
            onClick={() => onAdd(item)}
            variant="primary"
            size="sm"
            className={`w-full ${modeTheme.primaryBtn}`}
            disabled={availableQty === 0 && shipmentMode !== 'old_invoice'}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>
    </div>
  )
}
